import { useState, FormEvent } from 'react';
import { supabase } from '@/lib/supabase';
import type { AdminSession } from './hooks/useAdminAuth';

interface Props {
  onLogin: (s: AdminSession) => void;
}

async function sha256(input: string): Promise<string> {
  const encoder = new TextEncoder();
  const hashBuffer = await crypto.subtle.digest('SHA-256', encoder.encode(input));
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

export default function AdminLogin({ onLogin }: Props) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Forgot password mode
  const [showForgot, setShowForgot] = useState(false);
  const [forgotUsername, setForgotUsername] = useState('');
  const [masterKey, setMasterKey] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetMsg, setResetMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [resetting, setResetting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!username || !password) {
      setError('请输入用户名和密码');
      return;
    }
    if (loading) return;
    setLoading(true);
    setError('');

    try {
      const hashedInput = await sha256(password);

      const { data, error: dbErr } = await supabase
        .from('cms_admins')
        .select('username, role, display_name, password_hash')
        .eq('username', username)
        .maybeSingle();

      if (dbErr) {
        console.error('Login DB error:', dbErr);
        setError('系统错误，请稍后重试');
        setLoading(false);
        return;
      }

      if (!data) {
        setLoading(false);
        setError('用户名或密码错误，请重试');
        return;
      }

      const passwordMatch = data.password_hash === hashedInput || data.password_hash === password;

      if (!passwordMatch) {
        setLoading(false);
        setError('用户名或密码错误，请重试');
        return;
      }

      if (data.password_hash === password && data.password_hash !== hashedInput) {
        await supabase.from('cms_admins').update({ password_hash: hashedInput }).eq('username', username);
      }

      let permissions: string[] = ['dashboard', 'customers', 'articles', 'cases', 'faqs', 'settings'];
      let roleDisplayName = data.role;
      const { data: roleData } = await supabase
        .from('admin_roles')
        .select('permissions, display_name')
        .eq('name', data.role)
        .maybeSingle();
      if (roleData?.permissions && Array.isArray(roleData.permissions)) {
        permissions = roleData.permissions;
      }
      if (roleData?.display_name) {
        roleDisplayName = roleData.display_name;
      }

      setLoading(false);

      onLogin({
        username: data.username,
        role: data.role,
        display_name: data.display_name || data.username,
        role_display_name: roleDisplayName,
        permissions,
      });
    } catch (err: any) {
      console.error('Login exception:', err);
      setError('系统错误：' + (err?.message || '请稍后重试'));
      setLoading(false);
    }
  };

  const handleReset = async (e: FormEvent) => {
    e.preventDefault();
    if (!forgotUsername || !masterKey || !newPassword) {
      setResetMsg({ type: 'error', text: '请填写所有必填字段' });
      return;
    }
    if (newPassword.length < 4) {
      setResetMsg({ type: 'error', text: '密码至少需要4位' });
      return;
    }
    setResetting(true);
    setResetMsg(null);

    try {
      const apiBase = import.meta.env.VITE_PUBLIC_API_URL || '';
      const resp = await fetch(`${apiBase}/api/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ master_key: masterKey, username: forgotUsername, new_password: newPassword }),
      });
      const data = await resp.json();

      if (!resp.ok || data?.error) {
        setResetMsg({ type: 'error', text: data?.message || '重置失败' });
      } else {
        setResetMsg({ type: 'success', text: data?.message || '密码重置成功！请返回登录' });
        setForgotUsername('');
        setMasterKey('');
        setNewPassword('');
      }
    } catch {
      setResetMsg({ type: 'error', text: '网络错误，请稍后重试' });
    }
    setResetting(false);
  };

  if (showForgot) {
    return (
      <div className="min-h-screen bg-[#f8f5f0] flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 mb-4">
              <div className="w-10 h-10 flex items-center justify-center bg-[#1a3a5c] rounded-xl">
                <i className="ri-key-2-line text-[#c9a96e] text-lg"></i>
              </div>
              <span className="font-bold text-[#1a3a5c] text-xl">重置密码</span>
            </div>
            <p className="text-gray-400 text-sm">使用主密钥重置管理员密码</p>
          </div>

          <div className="bg-white rounded-2xl p-8">
            <form onSubmit={handleReset} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-[#1a3a5c] mb-1.5">管理员用户名 *</label>
                <input
                  type="text"
                  value={forgotUsername}
                  onChange={(e) => setForgotUsername(e.target.value)}
                  placeholder="输入要重置的管理员用户名"
                  className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#c9a96e]"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#1a3a5c] mb-1.5">主密钥 *</label>
                <input
                  type="password"
                  value={masterKey}
                  onChange={(e) => setMasterKey(e.target.value)}
                  placeholder="输入主重置密钥"
                  className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#c9a96e]"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#1a3a5c] mb-1.5">新密码 *</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="设置新密码（至少4位）"
                  className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#c9a96e]"
                />
              </div>

              {resetMsg && (
                <div className={`flex items-center gap-2 text-xs rounded-lg px-3 py-2 ${
                  resetMsg.type === 'success' ? 'text-green-600 bg-green-50' : 'text-red-500 bg-red-50'
                }`}>
                  <i className={`${resetMsg.type === 'success' ? 'ri-checkbox-circle-fill' : 'ri-error-warning-line'}`}></i>
                  {resetMsg.text}
                </div>
              )}

              <button
                type="submit"
                disabled={resetting}
                className="w-full bg-[#1a3a5c] hover:bg-[#0d2137] disabled:opacity-60 text-white font-semibold py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                {resetting ? '重置中...' : '重置密码'}
              </button>

              <button
                type="button"
                onClick={() => { setShowForgot(false); setResetMsg(null); }}
                className="w-full text-gray-400 hover:text-gray-600 text-sm cursor-pointer py-2 whitespace-nowrap"
              >
                <i className="ri-arrow-left-line mr-1"></i>返回登录
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f5f0] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-10 h-10 flex items-center justify-center bg-[#1a3a5c] rounded-xl">
              <i className="ri-shield-keyhole-line text-[#c9a96e] text-lg"></i>
            </div>
            <span className="font-bold text-[#1a3a5c] text-xl">RTB CMS</span>
          </div>
          <h1 className="text-2xl font-bold text-[#1a3a5c] mb-1">管理后台登录</h1>
          <p className="text-gray-400 text-sm">仅限授权管理员访问</p>
        </div>

        <div className="bg-white rounded-2xl p-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-[#1a3a5c] mb-1.5">用户名</label>
              <div className="relative">
                <i className="ri-user-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="请输入管理员用户名"
                  className="w-full border border-gray-200 rounded-lg pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:border-[#c9a96e]"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#1a3a5c] mb-1.5">密码</label>
              <div className="relative">
                <i className="ri-lock-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="请输入密码"
                  className="w-full border border-gray-200 rounded-lg pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:border-[#c9a96e]"
                />
              </div>
            </div>
            {error && (
              <div className="flex items-center gap-2 text-red-500 text-xs bg-red-50 rounded-lg px-3 py-2">
                <i className="ri-error-warning-line"></i>
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#1a3a5c] hover:bg-[#0d2137] disabled:opacity-60 text-white font-semibold py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap text-sm"
            >
              {loading ? '验证中...' : '登录管理后台'}
            </button>

            <div className="text-center pt-2">
              <button
                type="button"
                onClick={() => { setShowForgot(true); setError(''); }}
                className="text-gray-400 hover:text-[#c9a96e] text-xs cursor-pointer whitespace-nowrap transition-colors"
              >
                忘记密码？使用主密钥重置
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}